import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiBookOpen, FiCalendar, FiUsers, FiSave, FiVideo } from 'react-icons/fi';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &:disabled {
    background-color: var(--background-secondary);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const MultiSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  min-height: 120px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  option {
    padding: 0.5rem;
  }
`;

const SelectedCount = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
  min-height: 44px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: var(--background-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--background-secondary);
    border-color: var(--border-dark);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const SaveButton = styled(Button)`
  background-color: var(--primary-color);
  color: var(--text-inverse);
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function EditElectiveModal({ isOpen, onClose, elective, onElectiveUpdated }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    instructorIds: [],
    maxStudents: 25,
    startDate: '',
    endDate: '',
    isActive: true,
    virtualLink: ''
  });
  const [instructors, setInstructors] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (elective) {
      // Get instructor IDs from elective.instructors array if available, otherwise fallback to instructor_id
      const instructorIds = elective.instructors && Array.isArray(elective.instructors) 
        ? elective.instructors.map(inst => inst.id)
        : (elective.instructor_id ? [elective.instructor_id] : []);
      
      // Handle MySQL boolean values (0/1) correctly
      const isActiveValue = elective.is_active === 1 || elective.is_active === true;
      
      setFormData({
        code: elective.code || '',
        name: elective.name || '',
        description: elective.description || '',
        instructorIds: instructorIds,
        maxStudents: elective.max_students || 25,
        startDate: elective.start_date ? elective.start_date.split('T')[0] : '',
        endDate: elective.end_date ? elective.end_date.split('T')[0] : '',
        isActive: isActiveValue,
        virtualLink: elective.virtual_link || ''
      });
      setErrors({});
    }
  }, [elective]);

  useEffect(() => {
    if (isOpen) {
      fetchInstructors();
    }
  }, [isOpen]);

  const fetchInstructors = async () => {
    try {
      const response = await apiService.getInstructors();
      setInstructors(response.instructors || []);
    } catch (error) {
      console.error('Error fetching instructors:', error);
      setInstructors([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Elective code is required';
    } else if (formData.code.length > 20) {
      newErrors.code = 'Elective code must be 20 characters or less';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Elective name is required';
    }

    if (!formData.instructorIds || formData.instructorIds.length === 0) {
      newErrors.instructorIds = 'At least one instructor is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'End date cannot be before start date';
    }

    if (formData.maxStudents < 1 || formData.maxStudents > 200) {
      newErrors.maxStudents = 'Maximum students must be between 1 and 200';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Ensure isActive is always included in the update
      const updateData = {
        ...formData,
        isActive: formData.isActive === true || formData.isActive === 1
      };
      
      console.log('Updating elective with data:', updateData);
      console.log('isActive value:', updateData.isActive, 'type:', typeof updateData.isActive);
      
      const response = await apiService.updateElective(elective.id, updateData);
      
      console.log('Update response:', response);
      
      onElectiveUpdated(response.elective);
      onClose();
      
    } catch (error) {
      console.error('Error updating elective:', error);
      setErrors({ submit: 'Failed to update elective. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!elective) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Edit Elective</ModalTitle>
              <CloseButton onClick={onClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label>
                    <FiBookOpen size={16} />
                    Elective Code
                  </Label>
                  <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    placeholder="e.g., ELEC101"
                  />
                  {errors.code && <ErrorMessage>{errors.code}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiBookOpen size={16} />
                    Elective Name
                  </Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter elective name"
                  />
                  {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>
                  <FiBookOpen size={16} />
                  Description
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter elective description"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <FiVideo size={16} />
                  Virtual Classroom Link (Microsoft Teams)
                </Label>
                <Input
                  type="url"
                  value={formData.virtualLink}
                  onChange={(e) => handleInputChange('virtualLink', e.target.value)}
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                />
                <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Optional: Add the Microsoft Teams meeting link for live class sessions
                </small>
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>
                    <FiUsers size={16} />
                    Instructors
                  </Label>
                  {user?.role === 'admin' ? (
                    <>
                      <MultiSelect
                        multiple
                        value={formData.instructorIds.map(id => String(id))}
                        onChange={(e) => {
                          const selectedIds = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                          handleInputChange('instructorIds', selectedIds);
                        }}
                        className={errors.instructorIds ? 'error' : ''}
                      >
                        {instructors.map(instructor => (
                          <option key={instructor.id} value={instructor.id}>
                            {instructor.first_name} {instructor.last_name}
                          </option>
                        ))}
                      </MultiSelect>
                      <SelectedCount>
                        {formData.instructorIds.length} instructor{formData.instructorIds.length !== 1 ? 's' : ''} selected
                        {formData.instructorIds.length === 0 && ' (Hold Ctrl/Cmd to select multiple)'}
                      </SelectedCount>
                      {errors.instructorIds && <ErrorMessage>{errors.instructorIds}</ErrorMessage>}
                    </>
                  ) : (
                    <>
                      <Input
                        type="text"
                        value={elective?.instructors && Array.isArray(elective.instructors) 
                          ? elective.instructors.map(inst => `${inst.first_name} ${inst.last_name}`).join(', ')
                          : (instructors.find(inst => formData.instructorIds.includes(inst.id))?.first_name + ' ' + instructors.find(inst => formData.instructorIds.includes(inst.id))?.last_name || 'Loading...')}
                        disabled
                        style={{ backgroundColor: 'var(--background-secondary)', color: 'var(--text-muted)' }}
                      />
                      <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                        Only admins can modify instructors
                      </small>
                    </>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiUsers size={16} />
                    Max Students
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    value={formData.maxStudents}
                    onChange={(e) => handleInputChange('maxStudents', parseInt(e.target.value))}
                  />
                  {errors.maxStudents && <ErrorMessage>{errors.maxStudents}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>
                    <FiCalendar size={16} />
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                  />
                  {errors.startDate && <ErrorMessage>{errors.startDate}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiCalendar size={16} />
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                  />
                  {errors.endDate && <ErrorMessage>{errors.endDate}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>
                  <FiBookOpen size={16} />
                  Status
                </Label>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => {
                    const newValue = e.target.value === 'active';
                    handleInputChange('isActive', newValue);
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
                <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  {formData.isActive 
                    ? 'Active: Elective is enabled and visible to students (if dates are valid)'
                    : 'Inactive: Elective is disabled and hidden from students'}
                </small>
              </FormGroup>

              {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

              <ButtonGroup>
                <CancelButton type="button" onClick={onClose} disabled={loading}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FiSave size={16} />
                      Update Elective
                    </>
                  )}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default EditElectiveModal;

